import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiComprasInstance, authHeaderCompras } from "../../redux/actions/compras/compras";

import { Form, Input, Select, Button, Row, Col, Typography, Space, InputNumber, DatePicker, Switch, Dropdown } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { CheckOutlined, MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import "./EventosPage.css";
import "./PaquetesPage.css";

const { Title, Text } = Typography;

const EMPTY_LINEA = () => ({
  key: Date.now() + Math.random(),
  id_variante: null,
  producto_nombre: "",
  cantidad: "",
  costo_unitario_subtotal: "",
  costo_unitario_impuesto: "",
  confirmed: false,
});

const EMPTY_PAGO = () => ({
  key: Date.now() + Math.random(),
  id_forma_pago: null,
  monto: "",
});

const fmt = (n) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CrearCompraPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const [proveedores, setProveedores] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [variantesOpts, setVariantesOpts] = useState([]);

  const [lineas, setLineas] = useState([EMPTY_LINEA()]);
  const [pagos, setPagos] = useState([]);
  const [registrarPago, setRegistrarPago] = useState(false);

  useEffect(() => {
    const headers = authHeaderCompras();
    apiComprasInstance.get("/proveedores", { headers }).then((r) => setProveedores((r.data?.items ?? []).map((p) => ({ label: p.nombre, value: p.id_proveedor })))).catch(() => {});
    apiComprasInstance.get("/formas-pago", { headers }).then((r) => setFormasPago((r.data?.items ?? []).map((f) => ({ label: f.nombre, value: f.id_forma_pago })))).catch(() => {});
    apiComprasInstance.get("/productos/variantes/buscar", { headers }).then((r) => {
      setVariantesOpts((r.data?.items ?? []).map((v) => ({
        label: `${v.producto_nombre} (${v.clave})`,
        value: v.id_variante,
        producto_nombre: v.producto_nombre,
      })));
    }).catch(() => {});
  }, []);

  const updateLinea = (index, field, value) => {
    setLineas((prev) => prev.map((l, i) => {
      if (i !== index) return l;
      if (field === "id_variante") {
        const opt = variantesOpts.find((o) => o.value === value);
        return { ...l, id_variante: value, producto_nombre: opt?.label || "" };
      }
      return { ...l, [field]: value };
    }));
  };

  const confirmLinea = (index) => {
    const l = lineas[index];
    if (!l.id_variante) return toast.warning("Selecciona un producto");
    if (l.cantidad === "" || l.cantidad == null) return toast.warning("Ingresa la cantidad");
    if (l.costo_unitario_subtotal === "" || l.costo_unitario_subtotal == null) return toast.warning("Ingresa el costo unitario");

    setLineas((prev) => {
      const updated = prev.map((item, i) => (i === index ? { ...item, confirmed: true } : item));
      const hasOpenRow = updated.some((item) => !item.confirmed);
      return hasOpenRow ? updated : [...updated, EMPTY_LINEA()];
    });
  };

  const editLinea = (index) => setLineas((prev) => prev.map((l, i) => (i === index ? { ...l, confirmed: false } : l)));
  const removeLinea = (index) => setLineas((prev) => {
    const next = prev.filter((_, i) => i !== index);
    const hasOpenRow = next.some((l) => !l.confirmed);
    return hasOpenRow || next.length === 0 ? next : [...next, EMPTY_LINEA()];
  });

  const addPago = () => setPagos((prev) => [...prev, EMPTY_PAGO()]);
  const updatePago = (index, field, value) => setPagos((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  const removePago = (index) => setPagos((prev) => prev.filter((_, i) => i !== index));

  const totalPreview = lineas
    .filter((l) => l.confirmed)
    .reduce((a, l) => a + (parseFloat(l.cantidad) || 0) * ((parseFloat(l.costo_unitario_subtotal) || 0) + (parseFloat(l.costo_unitario_impuesto) || 0)), 0);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validLineas = lineas
      .filter((l) => l.confirmed && l.id_variante)
      .map((l) => ({
        id_variante: l.id_variante,
        cantidad: parseFloat(l.cantidad),
        costo_unitario_subtotal: parseFloat(l.costo_unitario_subtotal),
        costo_unitario_impuesto: l.costo_unitario_impuesto !== "" ? parseFloat(l.costo_unitario_impuesto) : 0,
      }));

    if (validLineas.length === 0) {
      toast.error("Agrega al menos un producto a la compra");
      return;
    }

    const validPagos = registrarPago
      ? pagos.filter((p) => p.id_forma_pago && p.monto !== "").map((p) => ({ id_forma_pago: p.id_forma_pago, monto: parseFloat(p.monto) }))
      : [];

    const payload = {
      id_proveedor: values.id_proveedor,
      folio_factura: values.folio_factura?.trim() || null,
      tiene_comprobante_fiscal: Boolean(values.tiene_comprobante_fiscal),
      fecha_vencimiento: values.fecha_vencimiento ? values.fecha_vencimiento.format("YYYY-MM-DD") : null,
      detalle: validLineas,
      pagos: validPagos,
    };

    setSaving(true);
    try {
      const { data } = await apiComprasInstance.post("/compras", payload, { headers: authHeaderCompras() });
      toast.success("Compra registrada exitosamente");
      navigate(`/app/compras/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="eventos-title" style={{ marginBottom: 0 }}>Nueva compra</Title>
            <Text className="eventos-subtitle">Registra una compra a un proveedor. La existencia se actualiza automaticamente.</Text>
          </Space>

          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate("/app/compras")} disabled={saving}>Cancelar</Button>
            <Button type="primary" loading={saving} icon={<i className="fa-solid fa-plus" />} onClick={handleSave} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
              Crear compra
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical">
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-cart-arrow-down"></i></span>
              Datos de la compra
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="id_proveedor" label={<span className="paq-field-label">Proveedor</span>} rules={[{ required: true, message: "Requerido" }]}>
                  <Select placeholder="Selecciona proveedor" options={proveedores} showSearch optionFilterProp="label" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="folio_factura" label={<span className="paq-field-label">Folio de factura (opcional)</span>}>
                  <Input placeholder="Folio de la factura" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="fecha_vencimiento" label={<span className="paq-field-label">Fecha de vencimiento (opcional)</span>}>
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" disabledDate={(d) => d && d < dayjs().startOf("day")} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="tiene_comprobante_fiscal" label={<span className="paq-field-label">Tiene comprobante fiscal</span>} valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="cc-section-card" style={{ marginTop: 20, paddingBottom: 20 }}>
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-list" /></span>
              Productos comprados
            </div>

            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 1000 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 6, padding: "0 8px" }}>
                  <div style={{ flex: 1, minWidth: 220, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Producto</div>
                  <div style={{ width: 130, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Cantidad</div>
                  <div style={{ width: 150, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Costo unitario</div>
                  <div style={{ width: 150, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Impuesto unitario</div>
                  <div style={{ width: 150, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Importe</div>
                  <div style={{ width: 130 }} />
                </div>

                {lineas.map((l, index) =>
                  l.confirmed ? (
                    <div key={l.key} style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 12px", marginBottom: 6, background: "#f8fafc", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                      <div style={{ flex: 1, minWidth: 220, fontSize: 13, fontWeight: 500, color: "#1f2937" }}>{l.producto_nombre}</div>
                      <div style={{ width: 130, fontSize: 13, color: "#374151" }}>{l.cantidad}</div>
                      <div style={{ width: 150, fontSize: 13, color: "#374151" }}>{fmt(l.costo_unitario_subtotal)}</div>
                      <div style={{ width: 150, fontSize: 13, color: "#374151" }}>{fmt(l.costo_unitario_impuesto || 0)}</div>
                      <div style={{ width: 150, fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
                        {fmt((parseFloat(l.cantidad) || 0) * ((parseFloat(l.costo_unitario_subtotal) || 0) + (parseFloat(l.costo_unitario_impuesto) || 0)))}
                      </div>
                      <div style={{ width: 130, display: "flex", justifyContent: "flex-end" }}>
                        <Dropdown
                          menu={{
                            items: [
                              { key: "edit", label: "Editar", icon: <i className="fa-solid fa-pen" /> },
                              { key: "delete", label: "Eliminar", icon: <i className="fa-solid fa-trash" />, danger: true },
                            ],
                            onClick: ({ key }) => (key === "edit" ? editLinea(index) : removeLinea(index)),
                          }}
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <button type="button" style={{ width: 32, height: 32, border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <MoreOutlined style={{ fontSize: 16, color: "#6b7280" }} />
                          </button>
                        </Dropdown>
                      </div>
                    </div>
                  ) : (
                    <div key={l.key} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, padding: "8px", background: "#fffdf5", borderRadius: 8, border: "1px dashed #d1d5db" }}>
                      <Select
                        style={{ flex: 1, minWidth: 220 }}
                        placeholder="Selecciona un producto"
                        options={variantesOpts}
                        value={l.id_variante || undefined}
                        onChange={(val) => updateLinea(index, "id_variante", val)}
                        showSearch
                        optionFilterProp="label"
                      />
                      <InputNumber style={{ width: 130 }} placeholder="Cantidad" min={0} precision={3} value={l.cantidad !== "" ? l.cantidad : undefined} onChange={(v) => updateLinea(index, "cantidad", v ?? "")} />
                      <InputNumber style={{ width: 150 }} placeholder="Costo unitario" min={0} precision={2} value={l.costo_unitario_subtotal !== "" ? l.costo_unitario_subtotal : undefined} onChange={(v) => updateLinea(index, "costo_unitario_subtotal", v ?? "")} />
                      <InputNumber style={{ width: 150 }} placeholder="Impuesto unitario" min={0} precision={2} value={l.costo_unitario_impuesto !== "" ? l.costo_unitario_impuesto : undefined} onChange={(v) => updateLinea(index, "costo_unitario_impuesto", v ?? "")} />
                      <div style={{ width: 130, display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <Button icon={<CheckOutlined />} onClick={() => confirmLinea(index)} className="eventos-btn-clean" style={{ width: 90 }}>Aceptar</Button>
                        <button type="button" className="paq-contenido-delete-btn" onClick={() => removeLinea(index)} title="Eliminar">
                          <i className="fa-solid fa-trash" />
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            <div style={{ textAlign: "right", marginTop: 12, fontSize: 15, fontWeight: 700, color: "#3d0011" }}>
              Total estimado: {fmt(totalPreview)}
            </div>
          </div>

          <div className="cc-section-card" style={{ marginTop: 20, paddingBottom: 20 }}>
            <div className="cc-section-header" style={{ justifyContent: "space-between", display: "flex" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="cc-section-icon"><i className="fa-solid fa-money-bill-wave" /></span>
                Registrar un pago ahora (opcional)
              </span>
              <Switch checked={registrarPago} onChange={setRegistrarPago} />
            </div>

            {registrarPago && (
              <div style={{ marginTop: 12 }}>
                {pagos.map((p, index) => (
                  <div key={p.key} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <Select style={{ width: 200 }} placeholder="Forma de pago" options={formasPago} value={p.id_forma_pago || undefined} onChange={(v) => updatePago(index, "id_forma_pago", v)} />
                    <InputNumber style={{ width: 160 }} placeholder="Monto" min={0} precision={2} value={p.monto !== "" ? p.monto : undefined} onChange={(v) => updatePago(index, "monto", v ?? "")} />
                    <button type="button" className="paq-contenido-delete-btn" onClick={() => removePago(index)}>
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                ))}
                <Button onClick={addPago} className="eventos-btn-clean">Agregar pago</Button>
              </div>
            )}
          </div>
        </Form>
      </div>
    </main>
  );
}
